import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getLibrary, putLibrary } from '../services/api';

const FavoritesContext = createContext(null);

export const FavoritesProvider = ({ children }) => {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [progress, setProgress] = useState({});
  const syncedFor = useRef(null);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const f = JSON.parse(localStorage.getItem('citytoon_favorites') || '[]');
      const p = JSON.parse(localStorage.getItem('citytoon_progress') || '{}');
      setFavorites(f);
      setProgress(p);
    } catch (e) { /* ignore */ }
  }, []);

  // When a user logs in: merge local data into backend, then use backend as source of truth
  useEffect(() => {
    if (!user) { syncedFor.current = null; return; }
    if (syncedFor.current === user.user_id) return;
    syncedFor.current = user.user_id;
    (async () => {
      try {
        const localF = JSON.parse(localStorage.getItem('citytoon_favorites') || '[]');
        const localP = JSON.parse(localStorage.getItem('citytoon_progress') || '{}');
        const merged = await putLibrary(localF, localP); // server merges + returns
        setFavorites(merged.favorites || []);
        setProgress(merged.progress || {});
        localStorage.setItem('citytoon_favorites', JSON.stringify(merged.favorites || []));
        localStorage.setItem('citytoon_progress', JSON.stringify(merged.progress || {}));
      } catch (e) {
        try { const lib = await getLibrary(); setFavorites(lib.favorites || []); setProgress(lib.progress || {}); } catch (_) { /* ignore */ }
      }
    })();
  }, [user]);

  const persist = (nextFav, nextProg) => {
    localStorage.setItem('citytoon_favorites', JSON.stringify(nextFav));
    localStorage.setItem('citytoon_progress', JSON.stringify(nextProg));
    if (user) { putLibrary(nextFav, nextProg).catch(() => {}); }
  };

  const toggleFavorite = (id) => {
    setFavorites((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      persist(next, progress);
      return next;
    });
  };

  const isFavorite = (id) => favorites.includes(id);

  const setReadProgress = (id, pct) => {
    setProgress((prev) => {
      const next = { ...prev, [id]: Math.max(prev[id] || 0, pct) };
      persist(favorites, next);
      return next;
    });
  };

  return (
    <FavoritesContext.Provider value={{ favorites, toggleFavorite, isFavorite, progress, setReadProgress }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error('useFavorites must be used within FavoritesProvider');
  return ctx;
};
